import { log, logE, logV } from "../../../shared/services/debug-logger";
import { HULL_PAINTS_MOCK_RESPONSE } from "../mocks/hull-paints.mocks";
import type { HullPaintsDashboardResponse } from "../models/hull-paints.models";
import { getHullPaintsDashboard, getHullPaintsErrorMessage } from "../services/hull-paints.service";
import { hullPaintsMockEnabledAtom } from "./hull-paints.atoms";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";

// Owns source selection, refresh, and request cleanup for this page only.
export const useHullPaintsDashboard = (enabled = true) => {
  const mockEnabled = useAtomValue(hullPaintsMockEnabledAtom);
  const [dashboard, setDashboard] = useState<HullPaintsDashboardResponse | null>(null);
  const [loadedMockEnabled, setLoadedMockEnabled] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshSequence, setRefreshSequence] = useState(0);

  const refresh = useCallback(() => {
    log("STATE - refresh - hull paints");
    setRefreshSequence((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      log("STATE - useHullPaintsDashboard - disabled");
      setDashboard(null);
      setLoadedMockEnabled(null);
      setError(null);
      setLoading(false);

      return;
    }

    const controller = new AbortController();
    let active = true;

    const load = async () => {
      log("STATE - load - hull paints", { mock: mockEnabled });
      setLoading(true);
      setError(null);
      setDashboard(null);
      setLoadedMockEnabled(null);

      try {
        const nextDashboard = mockEnabled
          ? HULL_PAINTS_MOCK_RESPONSE
          : await getHullPaintsDashboard(controller.signal);

        if (active) {
          setDashboard(nextDashboard);
          setLoadedMockEnabled(mockEnabled);
          log("STATE - load - hull paints applied", { vessels: nextDashboard.vessels.length, paints: nextDashboard.paints.length });
        } else {
          logV("STATE - load - hull paints stale result");
        }
      } catch (requestError) {
        if (active && !controller.signal.aborted) {
          logE(requestError, { operation: "STATE - load - hull paints failed" });
          setError(getHullPaintsErrorMessage(requestError));
          setDashboard(null);
          setLoadedMockEnabled(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [enabled, mockEnabled, refreshSequence]);

  const sourceMatches = loadedMockEnabled === mockEnabled;

  return {
    dashboard: sourceMatches ? dashboard : null,
    error,
    loading: enabled && (loading || !sourceMatches),
    mockEnabled,
    refresh,
  };
};
